import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { useChatSidebar } from "@v2/features/chat/hooks/useChatSidebar";
import {
  ArrowRight,
  ChevronDown,
  History,
  MonitorSmartphone,
  PanelLeft,
  Settings2,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { useEditorTutorial } from "./EditorTutorialContext";
import { NavItemWithAnnotation } from "./NavItemWithAnnotation";

export function EditorNavigationTutorial() {
  const tutorial = useEditorTutorial();
  const { open: isChatSidebarOpen } = useChatSidebar();
  const isVisible =
    Boolean(tutorial?.isActive) && tutorial?.currentStep === "navigation";

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 hidden bg-black/50 md:block"
            aria-hidden="true"
          />
          <motion.div
            key="content"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={twMerge(
              "absolute inset-x-0 top-0 z-50 hidden p-4 md:block",
              isChatSidebarOpen && "pl-[266px]",
            )}
          >
            <NavigationGuideContent onFinish={tutorial.onDismiss} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface NavigationGuideContentProps {
  onFinish: () => void;
}

function NavigationGuideContent({ onFinish }: NavigationGuideContentProps) {
  const [animationStarted, setAnimationStarted] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setAnimationStarted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex w-full flex-col gap-4 rounded-xl bg-black p-4">
      {/* Nav mockup with annotations */}
      <div className="relative min-h-[280px]">
        {/* Nav bar mockup */}
        <div className="pointer-events-none flex items-start gap-2">
          {/* Chat panel toggle - separate area (1/3 width) */}
          <div className="basis-1/3">
            <NavItemWithAnnotation
              label={`Access all chats
and create new ones`}
              lineHeight={80}
              delay={0}
              started={animationStarted}
            >
              <div className="flex h-12 w-full items-center gap-3 rounded-lg border border-border bg-white px-3">
                <Button variant="tertiary" icon>
                  <PanelLeft />
                </Button>
                <span className="text-sm font-medium text-default">
                  New chat
                </span>
              </div>
            </NavItemWithAnnotation>
          </div>

          {/* Rest of controls - separate area (2/3 width) */}
          <div className="flex h-12 basis-2/3 items-center justify-between rounded-lg border border-border bg-white px-3">
            {/* Left side - Page selector and action icons */}
            <div className="flex items-center gap-2">
              {/* Page switcher */}
              <NavItemWithAnnotation
                label={`Switch between pages
and create new ones`}
                lineHeight={80}
                delay={1000}
                started={animationStarted}
              >
                <div className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5">
                  <span className="text-sm font-medium text-black">
                    Restaurant Landing ...
                  </span>
                  <span className="rounded bg-subtle px-1.5 py-0.5 text-[10px] font-medium text-muted">
                    Draft
                  </span>
                  <ChevronDown className="size-3.5 text-muted" />
                </div>
              </NavItemWithAnnotation>

              {/* Action icons */}
              <NavItemWithAnnotation
                label={`Version
History`}
                lineHeight={130}
                delay={2000}
                started={animationStarted}
              >
                <Button variant="tertiary" icon>
                  <History />
                </Button>
              </NavItemWithAnnotation>
              <NavItemWithAnnotation
                label={`Page
Settings`}
                lineHeight={180}
                delay={3000}
                started={animationStarted}
              >
                <Button variant="tertiary" icon>
                  <Settings2 />
                </Button>
              </NavItemWithAnnotation>
            </div>

            {/* Right side buttons */}
            <div className="flex items-center gap-2">
              <NavItemWithAnnotation
                label={`Switch to
Mobile`}
                lineHeight={80}
                delay={4000}
                started={animationStarted}
              >
                <Button variant="tertiary" icon>
                  <MonitorSmartphone />
                </Button>
              </NavItemWithAnnotation>
              <NavItemWithAnnotation
                label={`Publish &
View Live Page`}
                lineHeight={130}
                delay={5000}
                started={animationStarted}
              >
                <Button
                  variant="primary"
                  size="base"
                  endEnhancer={<ChevronDown />}
                >
                  Publish
                </Button>
              </NavItemWithAnnotation>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-white/20 pt-4">
        <h2 className="text-lg font-semibold text-white">
          Navigate Your Editor
        </h2>
        <Button
          variant="secondary"
          size="base"
          onClick={onFinish}
          endEnhancer={<ArrowRight />}
        >
          Finish
        </Button>
      </div>
    </div>
  );
}
